import { useEffect, useState } from 'react'
import {
  alergiasApi,
  auxiliarApi, postWithOutboxFallback, refreshQrsCache,
} from '../lib/api'
import {
  deleteQrFromCache, findQrLocal, isQrCacheExpired,
} from '../lib/db'
import { useAuth } from '../hooks/useAuth'
import QRScanner from '../components/QRScanner'
import OfertaMenu from '../components/OfertaMenu'
import OutboxIndicator from '../components/OutboxIndicator'
import CancelarComidaDialog from '../components/CancelarComidaDialog'
import BannerAnafilactico from '../components/BannerAnafilactico'
import AlergiaInicialModal from '../components/AlergiaInicialModal'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmDialog'
import {
  tieneAlergiaAnafilactica,
  usePacienteActualStore,
} from '../stores/pacienteActualStore'

// QA 5-jul: FastAPI devuelve `detail` como STRING (errores propios) o como
// ARRAY de objetos (errores de validación 422). Interpolar el array daba
// "[object Object]" en el toast. Este helper siempre produce texto legible.
function msgError(err: any): string {
  const d = err?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join('; ')
  if (d && typeof d === 'object') return d.msg || 'Solicitud inválida'
  return err?.message || 'Error desconocido'
}

interface ComidaPendiente {
  id: number
  uuid: string
  paciente_id?: number // Sprint 5 T12-v2: requerido para alergias. Tolerante a ausencia (degradacion).
  cliente_nombre?: string
  habitacion?: string
  dieta_nombre?: string
  tiempo_nombre?: string
  estado: string
  lineas: any[]
  notas_paciente?: string
  created_at?: string
  updated_at?: string | null
}

function formatoTiempoLSA(iso?: string | null): string {
  if (!iso) return '—'
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (min < 1) return '< 1 min'
  if (min < 60) return `${min} min`
  return `${Math.floor(min / 60)}h ${min % 60}m`
}

// Mapeo estado actual → tipo_evento que dispara el siguiente paso del flujo.
// Usado en modo offline para encolar el evento correcto sin pegarle al backend.
function inferirTipoEventoQr(estado: string): 'QR_PEDIDO' | 'QR_ENTREGA' | null {
  if (estado === 'ASIGNADA') return 'QR_PEDIDO'
  if (estado === 'LISTA' || estado === 'EN_TRANSITO') return 'QR_ENTREGA'
  return null
}

function inferirEstadoNuevo(tipo: 'QR_PEDIDO' | 'QR_ENTREGA'): string {
  return tipo === 'QR_PEDIDO' ? 'EN_OFERTA' : 'EN_TRANSITO'
}

export default function AuxiliarDashboard() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const confirm = useConfirm()
  const [comidas, setComidas] = useState<ComidaPendiente[]>([])
  const [scanning, setScanning] = useState(false)
  const [comidaActiva, setComidaActiva] = useState<ComidaPendiente | null>(null)
  const [ofertaModalAbierto, setOfertaModalAbierto] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [comidaACancelar, setComidaACancelar] = useState<ComidaPendiente | null>(null)
  // Sprint 5 T12-v2: comida que dispara modal de alergias (primer escaneo del paciente).
  const [comidaParaAlergias, setComidaParaAlergias] = useState<ComidaPendiente | null>(null)
  const esAdmin = user?.rol === 'admin'

  const alergiasActuales = usePacienteActualStore((s) => s.alergiasPacienteActual)
  const setPacienteActual = usePacienteActualStore((s) => s.setPacienteActual)
  const setAlergiasStore = usePacienteActualStore((s) => s.setAlergias)
  const yaFueConsultadoHoy = usePacienteActualStore((s) => s.yaFueConsultadoHoy)
  const marcarConsultado = usePacienteActualStore((s) => s.marcarConsultado)
  const resetConsultasViejas = usePacienteActualStore((s) => s.resetConsultasViejas)
  const mostrarBanner = tieneAlergiaAnafilactica(alergiasActuales)
  const alergeno = mostrarBanner
    ? alergiasActuales.find(
        (a) => a.tipo === 'ALERGIA' && a.severidad === 'ANAFILACTICA' && a.activa && !a.contradicha,
      )?.alimento_nombre ?? 'desconocido'
    : ''

  async function refresh() {
    try {
      const data = await auxiliarApi.pendientes()
      setComidas(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 15_000)
    return () => clearInterval(interval)
  }, [])

  // Sprint 5 T12-v2: limpia el set de "ya consultados" cuando arranca un nuevo dia.
  useEffect(() => {
    resetConsultasViejas()
  }, [resetConsultasViejas])

  // Sprint 2 Tarea 2.6 — listeners online/offline para banner + decisión offline-aware
  useEffect(() => {
    const onOnline = () => setIsOnline(true)
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  async function handleQR(token: string) {
    setScanning(false)
    if (!comidaActiva) {
      showToast('Selecciona primero una comida', 'warning')
      return
    }

    const tipoEvento = inferirTipoEventoQr(comidaActiva.estado)
    if (!tipoEvento) {
      showToast(`No se puede escanear QR en estado ${comidaActiva.estado}`, 'warning')
      return
    }

    // ── Modo OFFLINE — validar contra cache local + encolar ──
    if (!navigator.onLine) {
      const entry = await findQrLocal(token)
      if (!entry) {
        showToast(
          'QR no reconocido. Reconecta a red para sincronizar el catálogo de QRs.',
          'error',
        )
        return
      }
      if (isQrCacheExpired(entry)) {
        // Layer 2: cache expirado pero seguimos offline → usamos la entry vieja
        console.warn('[QR] cache expirado, usando entry local hasta volver online')
      }
      await postWithOutboxFallback(
        '/auxiliar/qr/scan',
        { qr_token: token, comida_elegida_id: comidaActiva.id },
        { comida_id: comidaActiva.id, tipo_evento: tipoEvento, qr_token: token },
      )
      // Optimistic UI: avanzar estado localmente
      const estadoNuevo = inferirEstadoNuevo(tipoEvento)
      setComidas((prev) =>
        prev.map((c) => (c.id === comidaActiva.id ? { ...c, estado: estadoNuevo } : c)),
      )
      if (estadoNuevo === 'EN_OFERTA') {
        setComidaActiva((prev) => prev ? { ...prev, estado: 'EN_OFERTA' } : null)
        setOfertaModalAbierto(true)
      }
      showToast(
        'Sin red — escaneo guardado offline. Se sincroniza al volver conexión.',
        'warning',
      )
      return
    }

    // ── Modo ONLINE — POST real con manejo de Layer 3 (404 reactiva) ──
    try {
      const res = await auxiliarApi.scanQR(token, comidaActiva.id)
      if (res.success) {
        showToast(res.mensaje, 'success')
        // S8 T35 fix: sincronizar comidaActiva con el estado_nuevo devuelto
        // por el backend ANTES de refresh(). Sin esto, el modal OfertaMenu
        // (que se renderiza si comidaActiva.estado === 'EN_OFERTA') nunca
        // aparece porque comidaActiva queda con estado='ASIGNADA' en memoria
        // -> pantalla en blanco.
        if (res.estado_nuevo) {
          const estadoNuevo = res.estado_nuevo as ComidaPendiente['estado']
          setComidaActiva((prev) =>
            prev ? { ...prev, estado: estadoNuevo } : null,
          )
          setScanning(false)
          // S8 T40: si paso a EN_OFERTA, evaluar alergias antes de abrir oferta.
          if (estadoNuevo === 'EN_OFERTA' && comidaActiva) {
            await continuarTrasScan({ ...comidaActiva, estado: estadoNuevo })
          }
        } else {
          setScanning(false)
        }
        await refresh()
      } else {
        showToast(res.mensaje, 'error')
      }
    } catch (err: any) {
      const status = err.response?.status
      // Layer 3: token rotado en servidor. Invalidar local + refetch + avisar al usuario.
      if (status === 404) {
        await deleteQrFromCache(token)
        try {
          await refreshQrsCache()
          showToast('El QR cambió. Volvé a escanearlo.', 'warning')
        } catch {
          showToast('QR desconocido y no pude refrescar el catálogo. Reintentá.', 'error')
        }
        return
      }
      // Network error → encolar mismo flow que offline
      const isNetworkError = !err.response
      if (isNetworkError) {
        await postWithOutboxFallback(
          '/auxiliar/qr/scan',
          { qr_token: token, comida_elegida_id: comidaActiva.id },
          { comida_id: comidaActiva.id, tipo_evento: tipoEvento, qr_token: token },
        )
        const estadoNuevo = inferirEstadoNuevo(tipoEvento)
        setComidas((prev) =>
          prev.map((c) => (c.id === comidaActiva.id ? { ...c, estado: estadoNuevo } : c)),
        )
        if (estadoNuevo === 'EN_OFERTA') {
          setComidaActiva((prev) => prev ? { ...prev, estado: 'EN_OFERTA' } : null)
          setOfertaModalAbierto(true)
        }
        showToast('Red intermitente — escaneo guardado offline.', 'warning')
        return
      }
      showToast(`Error: ${msgError(err)}`, 'error')
    }
  }

  // Sprint 8 T40: el QR es el GATE inicial. El auxiliar llega a la cama,
  // escanea el QR de la puerta y SOLO ENTONCES la app le habilita interactuar
  // con el paciente (alergias + oferta). Antes el flujo mostraba info
  // clinica + alergias ANTES del scan, riesgo operativo: el auxiliar podia
  // ver datos de un paciente sin estar fisicamente en su cama.
  //
  // Nuevo orden:
  //   1. tarjeta ASIGNADA -> escanear QR directo (sin info previa)
  //   2. backend valida + cambia a EN_OFERTA + retorna estado_nuevo
  //   3. handleQR (success EN_OFERTA) chequea alergias del paciente:
  //      a. ya consultado hoy O alergias previas registradas: abre OfertaMenu
  //      b. paciente nuevo: abre modal alergias -> al completar abre OfertaMenu
  async function iniciarEscaneoPedido(c: ComidaPendiente) {
    setComidaActiva(c)
    setScanning(true)
  }

  // Helper: tras scan exitoso (EN_OFERTA), decidir si capturar alergias
  // antes de mostrar OfertaMenu, o abrirlo directo.
  async function continuarTrasScan(c: ComidaPendiente) {
    // S8 T45: sincronizar opciones del menu vigente con las lineas actuales
    // de la comida. Si la nutricionista creo opciones nuevas despues de
    // materializar, aqui las traemos antes de mostrarle al paciente.
    try {
      const res = await auxiliarApi.refrescarOpciones(c.id)
      if (res.lineas_nuevas_agregadas > 0) {
        await refresh()  // recarga comidas para tener las nuevas lineas
      }
    } catch (err) {
      // No bloqueante: si refrescar falla, continuamos con las lineas que ya hay
      console.warn('refrescarOpciones fallo, continuando con lineas actuales:', err)
    }

    if (c.paciente_id && !yaFueConsultadoHoy(c.paciente_id)) {
      const existentes = await alergiasApi.listar(c.paciente_id)
      setPacienteActual(c.paciente_id, existentes)
      if (existentes.length > 0) {
        marcarConsultado(c.paciente_id, 'CONFIRMADO_CON_ALERGIAS')
        setOfertaModalAbierto(true)
        return
      }
      // Sin alergias previas registradas: capturar primero, luego abrir oferta
      // (el onCompletar del modal de alergias dispara setOfertaModalAbierto).
      setComidaParaAlergias({ ...c, estado: 'EN_OFERTA' })
      return
    }
    // Ya consultado, o sin paciente_id (legacy): abrir oferta directo
    setOfertaModalAbierto(true)
  }

  async function marcarEntregada(comida: ComidaPendiente) {
    const ok = await confirm({
      title: 'Confirmar entrega',
      message: `¿Confirmar que entregaste la dieta de la Hab. ${comida.habitacion ?? ''}?`,
      confirmText: 'Sí, entregada',
    })
    if (!ok) return
    try {
      const r = await postWithOutboxFallback(
        `/auxiliar/comida/${comida.id}/entregar`,
        {},
        { comida_id: comida.id, tipo_evento: 'DIETA_ENTREGADA' },
      )
      if (r.status === 'queued') {
        // Optimistic: avanzar a ENTREGADA localmente
        setComidas((prev) =>
          prev.map((c) => (c.id === comida.id ? { ...c, estado: 'ENTREGADA' } : c)),
        )
        showToast('Sin red — entrega guardada offline.', 'warning')
      } else {
        await refresh()
      }
    } catch (err: any) {
      showToast(`Error: ${msgError(err)}`, 'error')
    }
  }

  const BTN = 'text-white px-4 rounded text-sm font-semibold'
  const btnStyle = (grad: string, sombra: string) => ({
    minHeight: 44,
    borderRadius: 14,
    background: grad,
    boxShadow: sombra,
  })

  // Orden real del día para agrupar (los nombres vienen del catálogo tiempos)
  const ORDEN_TIEMPO: Record<string, number> = {
    'Desayuno': 1, 'Refrigerio AM': 2, 'Almuerzo': 3,
    'Refrigerio PM': 4, 'Cena': 5, 'Refrigerio Nocturno': 6,
  }

  // Agrupar por tiempo de comida (orden del día) y dentro por habitación,
  // para que el auxiliar valide de un vistazo cuántas bandejas hay de cada
  // tiempo y de cada dieta.
  const grupos = (() => {
    const m = new Map<string, ComidaPendiente[]>()
    for (const c of comidas) {
      const k = c.tiempo_nombre || 'Sin tiempo'
      const arr = m.get(k) ?? []
      arr.push(c)
      m.set(k, arr)
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.habitacion || '').localeCompare(b.habitacion || ''))
    }
    return Array.from(m.entries()).sort(
      (a, b) => (ORDEN_TIEMPO[a[0]] ?? 99) - (ORDEN_TIEMPO[b[0]] ?? 99),
    )
  })()

  const porDieta = (() => {
    const m = new Map<string, number>()
    for (const c of comidas) {
      const k = c.dieta_nombre || '—'
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1])
  })()

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
            Auxiliar — Bandejas
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo}</p>
        </div>
        <OutboxIndicator />
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>

      {/* Sprint 5 T12-v2 (C10): banner anafilactico persistente bajo el header. */}
      {mostrarBanner && <BannerAnafilactico alergeno={alergeno} />}

      {!isOnline && (
        <div
          className="text-center text-sm py-2 sticky top-[64px] z-10 font-semibold"
          style={{ background: 'rgba(245,166,35,.18)', color: '#F5A623', borderBottom: '1px solid rgba(245,166,35,.4)' }}
        >
          Sin red — los cambios se guardan en el teléfono y se sincronizan al volver la conexión
        </div>
      )}

      <main className="p-4 mx-auto" style={{ maxWidth: 1200 }}>
        {loading && <p className="text-[#94A0CD]">Cargando...</p>}
        {!loading && comidas.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center max-w-2xl mx-auto">
            <p className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
              Sin bandejas pendientes
            </p>
            <p className="text-sm text-gray-500">
              Cuando enfermería indique una dieta, la tarjeta aparecerá aquí sola
              (la lista se actualiza cada 15 segundos).
            </p>
          </div>
        )}

        {/* Resumen: total y desglose por dieta */}
        {comidas.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              className="text-sm px-3 py-1 rounded-full font-bold"
              style={{ fontFamily: 'JetBrains Mono', color: '#fff', background: 'linear-gradient(120deg,#006EB4,#143C78)' }}
            >
              {comidas.length} bandeja{comidas.length === 1 ? '' : 's'}
            </span>
            {porDieta.map(([dieta, n]) => (
              <span
                key={dieta}
                className="text-xs px-2.5 py-1 rounded-full font-semibold"
                style={{ color: '#7FD4FF', background: 'rgba(0,110,180,.14)', border: '1px solid rgba(0,110,180,.5)' }}
              >
                {dieta} <span style={{ fontFamily: 'JetBrains Mono' }}>×{n}</span>
              </span>
            ))}
          </div>
        )}

        {grupos.map(([tiempo, lista]) => (
        <section key={tiempo} className="mb-6">
          <h2
            className="text-[11px] uppercase tracking-[0.14em] font-semibold mb-2 flex items-center gap-2"
            style={{ color: '#8B97C8' }}
          >
            {tiempo}
            <span
              className="px-2 py-0.5 rounded-full text-[10px]"
              style={{ fontFamily: 'JetBrains Mono', color: '#7FD4FF', background: 'rgba(0,110,180,.14)', border: '1px solid rgba(0,110,180,.4)' }}
            >
              {lista.length}
            </span>
          </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {lista.map((c) => (
          <div key={c.id} className="bg-white rounded-lg shadow p-4 flex flex-col">
            <div className="flex justify-between items-start mb-2 gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate" style={{ fontFamily: 'Space Grotesk' }}>
                  Hab. <span style={{ fontFamily: 'JetBrains Mono', color: '#7FD4FF' }}>{c.habitacion}</span> — {c.cliente_nombre}
                </p>
                <p className="text-sm text-gray-600 truncate">
                  {c.dieta_nombre} · {c.tiempo_nombre}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  en {c.estado.replace('_', ' ').toLowerCase()}:{' '}
                  <strong style={{ fontFamily: 'JetBrains Mono' }}>{formatoTiempoLSA(c.updated_at ?? c.created_at)}</strong>
                </p>
              </div>
              <EstadoBadge estado={c.estado} />
            </div>

            <div className="flex gap-2 mt-3 flex-wrap">
              {c.estado === 'ASIGNADA' && (
                <button
                  onClick={() => iniciarEscaneoPedido(c)}
                  className={BTN}
                  style={btnStyle('linear-gradient(120deg,#006EB4,#143C78)', '0 10px 22px -8px rgba(0,110,180,.7)')}
                >
                  📷 Escanear QR (Pedido)
                </button>
              )}
              {c.estado === 'EN_OFERTA' && (
                <button
                  onClick={async () => {
                    setComidaActiva(c)
                    // S8 T45: sincronizar opciones del menu vigente antes de mostrar
                    try {
                      const r = await auxiliarApi.refrescarOpciones(c.id)
                      if (r.lineas_nuevas_agregadas > 0) await refresh()
                    } catch (err) {
                      console.warn('refrescarOpciones fallo, continuando:', err)
                    }
                    // Aseguramos cache de alergias en memoria al entrar a OfertaMenu.
                    if (c.paciente_id) {
                      const a = await alergiasApi.listar(c.paciente_id)
                      setPacienteActual(c.paciente_id, a)
                    }
                    setOfertaModalAbierto(true)
                  }}
                  className={BTN}
                  style={btnStyle('linear-gradient(120deg,#0a86d4,#006EB4)', '0 10px 22px -8px rgba(0,110,180,.7)')}
                >
                  🍽 Ofrecer menú
                </button>
              )}
              {c.estado === 'LISTA' && (
                <button
                  onClick={async () => {
                    setComidaActiva(c)
                    if (c.paciente_id) {
                      const a = await alergiasApi.listar(c.paciente_id)
                      setPacienteActual(c.paciente_id, a)
                    }
                    setScanning(true)
                  }}
                  className={BTN}
                  style={btnStyle('linear-gradient(120deg,#006EB4,#143C78)', '0 10px 22px -8px rgba(0,110,180,.7)')}
                >
                  📷 Escanear QR (Entrega)
                </button>
              )}
              {c.estado === 'EN_TRANSITO' && (
                <button
                  onClick={() => marcarEntregada(c)}
                  className={BTN}
                  style={btnStyle('linear-gradient(120deg,#1d9e66,#14684a)', '0 10px 22px -8px rgba(47,224,138,.4)')}
                >
                  ✓ Marcar entregada
                </button>
              )}
              {esAdmin && c.estado !== 'ENTREGADA' && c.estado !== 'CANCELADA' && (
                <button
                  onClick={() => setComidaACancelar(c)}
                  className={`${BTN} ml-auto`}
                  style={btnStyle('linear-gradient(120deg,#FF5C7A,#c23a5a)', '0 10px 22px -8px rgba(255,92,122,.4)')}
                >
                  ⛔ Cancelar (admin)
                </button>
              )}
            </div>
          </div>
        ))}
        </div>
        </section>
        ))}

        {scanning && <QRScanner onScan={handleQR} onCancel={() => setScanning(false)} />}
        {comidaActiva && ofertaModalAbierto && (
          <OfertaMenu
            comida={comidaActiva}
            onClose={() => { setComidaActiva(null); setOfertaModalAbierto(false); refresh() }}
          />
        )}
        {comidaACancelar && (
          <CancelarComidaDialog
            comida_id={comidaACancelar.id}
            comida_label={`Hab. ${comidaACancelar.habitacion ?? '?'} — ${comidaACancelar.cliente_nombre ?? ''} · ${comidaACancelar.dieta_nombre ?? ''} · ${comidaACancelar.tiempo_nombre ?? ''}`}
            onClose={() => setComidaACancelar(null)}
            onSuccess={() => refresh()}
          />
        )}

        {/* Sprint 5 T12-v2 (Δ7): captura inicial de alergias antes del primer scan. */}
        {comidaParaAlergias && comidaParaAlergias.paciente_id && (
          <AlergiaInicialModal
            pacienteId={comidaParaAlergias.paciente_id}
            pacienteNombre={comidaParaAlergias.cliente_nombre}
            habitacion={comidaParaAlergias.habitacion}
            onCompletar={(estado, creadas) => {
              const pid = comidaParaAlergias.paciente_id!
              marcarConsultado(pid, estado)
              setAlergiasStore(creadas)
              setComidaParaAlergias(null)
              // S8 T40: tras capturar alergias, abrir OfertaMenu (la comida
              // ya esta en EN_OFERTA porque el scan fue PRIMERO).
              setOfertaModalAbierto(true)
            }}
            onCancelar={() => {
              // S8 T40: la comida YA paso a EN_OFERTA via el scan previo.
              // Cancelar alergias no revierte el estado; el auxiliar puede
              // tocar "Ofrecer menu" desde la tarjeta EN_OFERTA cuando quiera.
              setComidaParaAlergias(null)
              setComidaActiva(null)
              showToast(
                'Alergias canceladas. La oferta queda pendiente — toca la tarjeta cuando estes listo.',
                'warning',
              )
            }}
          />
        )}
      </main>
    </div>
  )
}

function EstadoBadge({ estado }: { estado: string }) {
  // Chips con los tokens Hospital-Tech (legibles sobre navy)
  const colors: Record<string, { bg: string; fg: string }> = {
    ASIGNADA:          { bg: 'rgba(148,160,205,.18)', fg: '#94A0CD' },
    EN_OFERTA:         { bg: 'rgba(0,110,180,.22)',   fg: '#7FD4FF' },
    OFERTA_CONFIRMADA: { bg: 'rgba(245,166,35,.18)',  fg: '#F5A623' },
    EN_PREPARACION:    { bg: 'rgba(245,166,35,.25)',  fg: '#F5A623' },
    LISTA:             { bg: 'rgba(47,224,138,.16)',  fg: '#2FE08A' },
    EN_TRANSITO:       { bg: 'rgba(127,212,255,.16)', fg: '#7FD4FF' },
    ENTREGADA:         { bg: 'rgba(47,224,138,.25)',  fg: '#2FE08A' },
  }
  const c = colors[estado] ?? colors.ASIGNADA
  return (
    <span
      className="text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.fg}44` }}
    >
      {estado.replace('_', ' ')}
    </span>
  )
}
